import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Eye, Cable } from "lucide-react";
import { useTableSort, SortTh } from "@/lib/tableSort";
import { InfraFilters } from "@/pages/NetworkOdc";
import { CableFormDialog } from "@/components/network/CableFormDialog";
import { CableDetailPanel } from "@/components/network/CableDetailPanel";
import { StatusPill } from "@/components/network/StatusPill";
import { DeleteInfraButton } from "@/components/network/DeleteInfraButton";
import { OldKodeLabel } from "@/components/network/OldKodeLabel";
import { InfraCards, InfraCard } from "@/components/network/InfraCards";
import { useNetworkRefs, CABLE_JENIS, CABLE_JENIS_LABEL, SELECT_CLS, fmtLen } from "@/lib/network";

const pt = (kind, o) => ({ kind, id: o.id, kode: o.kode, wilayah_id: o.wilayah_id, latitude: o.latitude, longitude: o.longitude });
const wil = (c) => c.to?.wilayah_id || c.from?.wilayah_id;
const hit = (c, q) => [c.kode, c.nama, ...(c.kode_lama || [])].some((v) => (v || "").toLowerCase().includes(q));

// Pilih titik awal (ODC/ODP) & tujuan (ODP) sebelum form kabel; rute bisa digambar nanti di Peta.
function EndpointDialog({ open, onClose, onNext }) {
  const [opts, setOpts] = useState({ odc: [], odp: [], otb: [] });
  const [s, setS] = useState({ kind: "odc", from: "", toKind: "odp", to: "" });
  useEffect(() => {
    if (!open) return;
    setS({ kind: "odc", from: "", toKind: "odp", to: "" });
    Promise.all([api.get("/network/odc"), api.get("/network/odp"), api.get("/network/otb")]).then(([a, b, c]) => setOpts({ odc: a.data, odp: b.data, otb: c.data }));
  }, [open]);
  const toKinds = s.kind === "otb" ? ["odp", "otb", "odc"] : ["odp", "otb"];
  const from = opts[s.kind].find((o) => o.id === s.from);
  const to = opts[s.toKind].find((o) => o.id === s.to);
  const sel = (k, list, tid) => (
    <select className={`${SELECT_CLS} w-full`} value={s[k]} onChange={(e) => setS((x) => ({ ...x, [k]: e.target.value }))} data-testid={tid}>
      <option value="">— Pilih —</option>{list.map((o) => <option key={o.id} value={o.id}>{o.kode} · {o.nama}</option>)}</select>);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="cable-endpoint-dialog">
        <DialogHeader><DialogTitle>Tambah Kabel — Titik Awal & Tujuan</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Jenis Titik Awal</Label>
            <select className={`${SELECT_CLS} w-full`} value={s.kind} onChange={(e) => setS({ kind: e.target.value, from: "", toKind: "odp", to: "" })} data-testid="cable-endpoint-kind">
              <option value="odc">ODC</option><option value="odp">ODP</option><option value="otb">OTB</option></select></div>
          <div><Label>Titik Awal</Label>{sel("from", opts[s.kind], "cable-endpoint-from")}</div>
          <div><Label>Jenis Tujuan</Label>
            <select className={`${SELECT_CLS} w-full`} value={s.toKind} onChange={(e) => setS((x) => ({ ...x, toKind: e.target.value, to: "" }))} data-testid="cable-endpoint-to-kind">
              {toKinds.map((k) => <option key={k} value={k}>{k.toUpperCase()}</option>)}</select></div>
          <div><Label>Tujuan</Label>{sel("to", opts[s.toKind].filter((o) => !(s.toKind === s.kind && o.id === s.from)), "cable-endpoint-to")}</div>
          <p className="text-xs text-[#6B7280]">Jalur awal berupa garis lurus. Belokan rute dapat digambar lewat Peta Jaringan.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="cable-endpoint-cancel">Batal</Button>
          <Button disabled={!from || !to} onClick={() => onNext({ editing: null, from: pt(s.kind, from), to: pt(s.toKind, to), points: [] })} data-testid="cable-endpoint-next">Lanjut</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function NetworkCable() {
  const { refs, wilayahName } = useNetworkRefs();
  const [f, setF] = useState({ q: "", lokasi_id: "", wilayah_id: "", status: "", jenis: "" });
  const [all, setAll] = useState(null);
  const [pick, setPick] = useState(false);
  const [draft, setDraft] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => api.get("/network/cables", { params: { lokasi_id: f.lokasi_id || undefined } }).then((r) => setAll(r.data)), [f.lokasi_id]);
  useEffect(() => { load(); }, [load]);
  const rows = useMemo(() => {
    const q = f.q.trim().toLowerCase();
    return all && all.filter((c) => (!q || hit(c, q)) && (!f.status || c.status === f.status) && (!f.jenis || c.jenis === f.jenis)
      && (!f.wilayah_id || wil(c) === f.wilayah_id));
  }, [all, f]);
  const getters = useMemo(() => ({ kode: (c) => c.kode, nama: (c) => c.nama, jenis: (c) => c.jenis, dari: (c) => c.from?.kode, ke: (c) => c.to?.kode,
    panjang: (c) => c.panjang_m, wilayah: (c) => wilayahName(wil(c)), status: (c) => c.status }), [wilayahName]);
  const st = useTableSort("cable-master", rows, getters);
  const canManage = !!refs?.me?.can_manage;
  const edit = (c) => { setDetail(null); setDraft({ editing: c, from: c.from && pt(c.from_kind, c.from), to: c.to && pt(c.to_kind || "odp", c.to), points: c.route || [] }); };
  const cols = [["kode", "Kode"], ["nama", "Nama"], ["jenis", "Jenis"], ["dari", "Dari"], ["ke", "Ke"], ["panjang", "Panjang"], ["wilayah", "Wilayah"], ["status", "Status"]];
  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid="network-cable-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="font-display text-2xl font-bold">Master Kabel</h1>
          <p className="text-sm text-[#6B7280]">Jalur kabel optik antar ODC/ODP/OTB{canManage ? "" : " (mode lihat)"}</p></div>
        {canManage && <Button onClick={() => setPick(true)} data-testid="cable-add-btn"><Plus className="mr-1 h-4 w-4" />Tambah Kabel</Button>}
      </div>
      <InfraFilters refs={refs} f={f} setF={setF} prefix="cable" placeholder="Cari kode, kode lama, nama…">
        <select className={SELECT_CLS} value={f.jenis} onChange={(e) => setF((x) => ({ ...x, jenis: e.target.value }))} data-testid="cable-filter-jenis">
          <option value="">Semua Jenis</option>{CABLE_JENIS.map((j) => <option key={j} value={j}>{CABLE_JENIS_LABEL[j]}</option>)}</select>
      </InfraFilters>
      <InfraCards prefix="cable-master" st={st} cols={cols}
                  empty={rows && !rows.length ? `Belum ada kabel${Object.values(f).some(Boolean) ? " untuk filter ini" : ""}.` : null}
                  render={(c) => (
        <InfraCard key={c.id} prefix="cable-master" row={c} q={f.q}
                   sub={<>{CABLE_JENIS_LABEL[c.jenis] || c.jenis} · {fmtLen(c.panjang_m)} · {wilayahName(wil(c))}</>}
                   info={<span data-testid={`cable-master-card-route-${c.kode}`}>{c.from ? `${c.from_kind.toUpperCase()} ${c.from.kode}` : <span className="text-[#B45309]">?</span>}
                     {" → "}{c.to ? `${(c.to_kind || "odp").toUpperCase()} ${c.to.kode}` : <span className="text-[#B45309]">?</span>}</span>}
                   actions={<>
                     <Button size="icon" variant="ghost" onClick={() => setDetail(c)} data-testid={`cable-master-card-view-${c.kode}`}><Eye className="h-4 w-4" /></Button>
                     {canManage && c.from && c.to && <Button size="icon" variant="ghost" onClick={() => edit(c)} data-testid={`cable-master-card-edit-${c.kode}`}><Pencil className="h-4 w-4" /></Button>}
                     <DeleteInfraButton iconOnly kind="cable" item={c} me={refs?.me} onDeleted={load} tid={`cable-master-card-delete-${c.kode}`} />
                   </>} />)} />
      <div className="hidden overflow-x-auto rounded-lg border border-[#E5E7EB] bg-white md:block">
        <table className="w-full min-w-[860px] text-sm" data-testid="cable-master-table">
          <thead className="bg-[#F8F9FA] text-left text-xs uppercase text-[#6B7280]">
            <tr>{cols.map(([c, l], i) => <SortTh key={c} className={i ? "" : "p-2"} col={c} label={l} s={st} testid="cable-master" />)}<th className="pr-2 text-right">Aksi</th></tr>
          </thead>
          <tbody>
            {(st.sorted || []).map((c) => (
              <tr key={c.id} className="border-t" data-testid={`cable-master-row-${c.kode}`}>
                <td className="p-2 font-mono-tab font-semibold">{c.kode}<OldKodeLabel row={c} q={f.q} testid={`cable-master-oldkode-${c.kode}`} /></td>
                <td>{c.nama || "-"}</td><td>{CABLE_JENIS_LABEL[c.jenis] || c.jenis}</td>
                <td>{c.from ? `${c.from_kind.toUpperCase()} ${c.from.kode}` : <span className="text-[#B45309]">?</span>}</td>
                <td>{c.to ? `${(c.to_kind || "odp").toUpperCase()} ${c.to.kode}` : <span className="text-[#B45309]">?</span>}</td>
                <td>{fmtLen(c.panjang_m)}</td><td>{wilayahName(wil(c))}</td><td><StatusPill status={c.status} /></td>
                <td className="whitespace-nowrap pr-2 text-right">
                  <Button size="icon" variant="ghost" onClick={() => setDetail(c)} data-testid={`cable-master-view-${c.kode}`}><Eye className="h-4 w-4" /></Button>
                  {canManage && c.from && c.to && <Button size="icon" variant="ghost" onClick={() => edit(c)} data-testid={`cable-master-edit-${c.kode}`}><Pencil className="h-4 w-4" /></Button>}
                  <DeleteInfraButton iconOnly kind="cable" item={c} me={refs?.me} onDeleted={load} tid={`cable-master-delete-${c.kode}`} />
                </td>
              </tr>
            ))}
            {rows && !rows.length && <tr><td colSpan={9} className="p-8 text-center text-[#6B7280]" data-testid="cable-master-empty">
              <Cable className="mx-auto mb-2 h-8 w-8 opacity-40" />Belum ada kabel{Object.values(f).some(Boolean) ? " untuk filter ini" : ""}.</td></tr>}
          </tbody>
        </table>
      </div>
      <EndpointDialog open={pick} onClose={() => setPick(false)} onNext={(d) => { setPick(false); setDraft(d); }} />
      <CableFormDialog draft={draft} open={!!draft} backLabel="Batal" onClose={() => setDraft(null)} onSaved={() => { setDraft(null); load(); }} />
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[92dvh] max-w-lg overflow-y-auto p-0 [&>button:last-child]:hidden" data-testid="cable-master-detail-dialog">
          <DialogHeader className="sr-only"><DialogTitle>Detail Kabel</DialogTitle></DialogHeader>
          {detail && <CableDetailPanel sel={{ kind: "cable", id: detail.id }} me={refs?.me} wilayahName={wilayahName} onClose={() => setDetail(null)}
                                       onSelect={() => {}} onEdit={edit} onDeleted={() => { setDetail(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
